import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from './axiosConfig';
import { Modal, Select } from 'antd';
import {
  BarChartOutlined, BugOutlined, CheckCircleOutlined, ClockCircleOutlined,
  ExclamationCircleOutlined, ExperimentOutlined,FieldTimeOutlined, InboxOutlined, RiseOutlined,
  StarOutlined, TeamOutlined, ThunderboltOutlined,
  CaretUpFilled, CaretDownFilled,
} from '@ant-design/icons';
import IncidentModal from './IncidentModal';
import { FadeIn, SkelCards, SkelList, SkelLines, SkelTable } from './Skeleton';
import { STATUS_META, RESOLVED_STATUSES } from './statusMeta';

const SK_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Máj', 'Jún', 'Júl', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTS = [
  { value: null, label: 'Celkovo' },
  ...Array.from({ length: CURRENT_YEAR - 2019 }, (_, i) => ({
    value: CURRENT_YEAR - i,
    label: String(CURRENT_YEAR - i),
  })),
];
const MONTH_OPTS = [
  { value: null, label: 'Celý rok' },
  ...SK_MONTHS.map((label, i) => ({ value: i + 1, label })),
];

// Business minutes → human readable (1 pd = 480 min = 8h of work)
const fmtTime = (minutes) => {
  if (!minutes && minutes !== 0) return '—';
  const m = Number(minutes);
  if (m < 60)  return `${m} min`;
  if (m < 480) return `${Math.round(m / 60)} hod`;
  return `${(m / 480).toFixed(1).replace(/\.0$/, '')} pd`;
};

const fmtMd = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  return `${n % 1 === 0 ? n : n.toFixed(1)} MD`;
};

const PRIORITY_LABEL = { 1: 'Urgentná', 2: 'Bežná' };


const SortIcon = ({ field, sortField, sortOrder }) => {
  if (field !== sortField) return <span style={{ color: '#3a3b3e', marginLeft: 3, fontSize: 10 }}>↕</span>;
  return sortOrder === 'asc'
    ? <CaretUpFilled   style={{ color: '#00b96b', marginLeft: 3, fontSize: 10 }} />
    : <CaretDownFilled style={{ color: '#00b96b', marginLeft: 3, fontSize: 10 }} />;
};

const KpiCard = ({ icon, label, value, color, sub, onClick }) => (
  <div
    className="kpi-card"
    onClick={onClick}
    style={{ cursor: onClick ? 'pointer' : 'default' }}
  >
    <div className="kpi-card-icon" style={{ color }}>{icon}</div>
    <div className="kpi-card-body">
      <div className="kpi-card-value" style={{ color, textDecoration: onClick ? 'underline dotted' : 'none' }}>
        {value ?? '—'}
      </div>
      <div className="kpi-card-label">{label}</div>
      {sub && <div className="kpi-card-sub">{sub}</div>}
    </div>
  </div>
);

/* ═══════════════════════════════════════
   KPI PAGE
═══════════════════════════════════════ */
const KPI = ({ loggedInUser }) => {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [companies, setCompanies]   = useState([]);
  const [categoryOpts, setCategoryOpts] = useState([]);
  const [company, setCompany]       = useState('');
  const [category, setCategory]     = useState('');
  const [selYear, setSelYear]       = useState(CURRENT_YEAR);
  const [selMonth, setSelMonth]     = useState(null);

  const filters = useRef({ company: '', category: '', year: CURRENT_YEAR, month: null });

  // KPI list modal (click on a card number)
  const [listModal, setListModal]     = useState({ open: false, title: '', status: null });
  const [listItems, setListItems]     = useState([]);
  const [listLoading, setListLoading] = useState(false);

  // Incident detail modal
  const [selIncident, setSelIncident]   = useState(null);
  const [incModalOpen, setIncModalOpen] = useState(false);

  // Table sort states
  const [sortReq, setSortReq] = useState({ field: 'total',    order: 'desc' });
  const [sortCat, setSortCat] = useState({ field: 'total',    order: 'desc' });

  /* ── fetch KPI data ── */
  const fetchData = (co, ca, yr, mo) => {
    filters.current = { company: co, category: ca, year: yr, month: mo };
    setLoading(true);
    const params = {};
    if (co) params.company  = co;
    if (ca) params.category = ca;
    if (yr) params.year     = yr;
    if (mo) params.month    = mo;
    axios.get('/kpi-stats', { params })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!loggedInUser) return;
    fetchData('', '', CURRENT_YEAR, null);
    axios.get('/companies').then(r => setCompanies(r.data || []));
    axios.get('/categories').then(r => {
      const opts = (r.data || [])
        .map(c => (typeof c === 'string' ? c : c?.category))
        .filter(v => v)
        .map(v => ({ value: v, label: v }));
      setCategoryOpts(opts);
    });
  }, [loggedInUser]);

  const handleCompany  = (v) => { const val = v || ''; setCompany(val);  fetchData(val,     category, selYear,  selMonth); };
  const handleCategory = (v) => { const val = v || ''; setCategory(val); fetchData(company, val,      selYear,  selMonth); };
  const handleYear     = (v) => { setSelYear(v);  setSelMonth(null); fetchData(company, category, v,       null);    };
  const handleMonth    = (v) => { setSelMonth(v);                    fetchData(company, category, selYear, v);        };

  /* ── open list modal for a card ── */
  const openListModal = (title, statuses) => {
    setListModal({ open: true, title, statuses });
    setListItems([]);
    setListLoading(true);
    const f = filters.current;
    const params = { pageSize: 100, page: 1, sortField: 'id', sortOrder: 'desc' };
    if (f.company)  params.company  = f.company;
    if (f.category) params.category = f.category;
    if (f.year)     params.year     = f.year;
    if (f.month)    params.month    = f.month;
    if (statuses)   params.statuses = statuses.join(',');
    axios.get('/incidents', { params })
      .then(r => setListItems(r.data?.data || r.data?.incidents || []))
      .catch(console.error)
      .finally(() => setListLoading(false));
  };

  /* ── open incident detail modal ── */
  const openIncModal = (id) => {
    axios.get(`/incident/get/${id}`)
      .then(r => { setSelIncident(r.data); setIncModalOpen(true); })
      .catch(console.error);
  };

  /* ── table sort helpers ── */
  const makeSort = (setSortState) => (field) =>
    setSortState(prev => ({ field, order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc' }));
  const handleSortReq = makeSort(setSortReq);
  const handleSortCat = makeSort(setSortCat);

  const sortRows = (rows, { field, order }) => {
    if (!rows) return [];
    return [...rows].sort((a, b) => {
      const av = a[field] ?? 0, bv = b[field] ?? 0;
      if (typeof av === 'string') return order === 'asc' ? av.localeCompare(bv, 'sk') : bv.localeCompare(av, 'sk');
      return order === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
  };

  const sortedRequesters = useMemo(() => sortRows(data?.topRequesters, sortReq), [data, sortReq]);
  const sortedCategories = useMemo(() => sortRows(data?.byCategory,    sortCat), [data, sortCat]);

  const s        = data?.summary;
  const byMonth  = data?.byMonth  || [];
  const maxCount = Math.max(1, ...byMonth.map(m => m.count));
  const topCatName = data?.byCategory?.[0]?.category;
  const io = data?.inflowOutflow;
  const effort = data?.effort;
  const hasEffort = effort && (effort.plannedCnt > 0 || effort.actualCnt > 0);
  const periodNote = selYear
    ? ` · ${selMonth ? `${SK_MONTHS[selMonth - 1]} ` : ''}${selYear}`
    : '';

  return (
    <div className="kpi-page">

      {/* ── top bar ── */}
      <div className="kpi-topbar">
        <div className="kpi-topbar-left">
          <BarChartOutlined style={{ color: '#00b96b', fontSize: 16, marginRight: 8 }} />
          <span className="kpi-topbar-title">KPI &amp; SLA Štatistiky</span>
          {selYear && (
            <span className="kpi-topbar-period">
              {selYear}{selMonth ? ` · ${SK_MONTHS[selMonth - 1]}` : ''}
            </span>
          )}
        </div>
        <div className="kpi-topbar-filters">
          {loggedInUser?.role !== 'USER' && (
            <Select
              style={{ width: 210 }}
              placeholder="Všetky spoločnosti"
              allowClear
              value={company || undefined}
              onChange={handleCompany}
              options={companies.map(c => ({ value: c.alias, label: `${c.name} (${c.alias})` }))}
              showSearch
              optionFilterProp="label"
            />
          )}
          <Select
            style={{ width: 230 }}
            placeholder="Všetky projekty"
            allowClear
            value={category || undefined}
            onChange={handleCategory}
            options={categoryOpts}
            showSearch
            optionFilterProp="label"
          />
          <Select
            style={{ width: 110 }}
            value={selYear}
            onChange={handleYear}
            options={YEAR_OPTS}
          />
          {selYear && (
            <Select
              style={{ width: 115 }}
              value={selMonth}
              onChange={handleMonth}
              options={MONTH_OPTS}
            />
          )}
        </div>
      </div>

      <div className={`kpi-body${!loading && data ? ' fade-in' : ''}`}>

        {/* ── KPI cards – status ── */}
        {loading && !data ? (
          <SkelCards count={8} />
        ) : (
        <div className="kpi-cards-row">
          <KpiCard icon={<ExclamationCircleOutlined />} label="Celkom"     value={s?.total}      color="#aaa"    onClick={() => openListModal('Všetky požiadavky', null)} />
          <KpiCard icon={<ExclamationCircleOutlined />} label="Prijaté"    value={s?.received}   color="#5abbff" onClick={() => openListModal('Prijaté', [1])} />
          <KpiCard icon={<ClockCircleOutlined />}       label="V riešení"  value={s?.inProgress} color="#e88e39" onClick={() => openListModal('V riešení', [2])} />
          <KpiCard icon={<ExperimentOutlined />}        label="Na teste"   value={s?.onTest}     color={STATUS_META[6].color} onClick={() => openListModal('Na teste', [6])} />
          <KpiCard icon={<CheckCircleOutlined />}       label="Vyriešené"  value={s?.resolved}   color="#52c41a" onClick={() => openListModal('Vyriešené', RESOLVED_STATUSES)} />
          <KpiCard icon={<ExclamationCircleOutlined />} label="Zamietnuté" value={s?.rejected}   color="#ff5a5a" onClick={() => openListModal('Zamietnuté', [4])} />
          <KpiCard icon={<InboxOutlined />}             label="Backlog"    value={s?.backlog}    color="#a78bfa" onClick={() => openListModal('Backlog', [5])} />
          <KpiCard
            icon={<ClockCircleOutlined />}
            label="Priem. čas riešenia"
            value={fmtTime(s?.avgMinutes)}
            color="#00b96b"
            sub="pracovné hodiny Po–Pá 8:00–16:00"
          />
          {topCatName && !category && (
            <KpiCard
              icon={<StarOutlined />}
              label="Najviac podporovaný projekt"
              value={topCatName}
              color="#a78bfa"
              sub={`${data.byCategory[0].total} požiadaviek`}
            />
          )}
        </div>
        )}

        {/* ── KPI cards – SLA / throughput / effort ── */}
        {loading && !data ? (
          <div style={{ marginTop: 14 }}><SkelCards count={4} /></div>
        ) : (
        <div className="kpi-cards-row">
          <KpiCard
            icon={<FieldTimeOutlined />}
            label="Medián času riešenia"
            value={fmtTime(s?.medianMinutes)}
            color="#5abbff"
            sub="odolnejší voči outlierom"
          />
          <KpiCard
            icon={<ThunderboltOutlined />}
            label="SLA ≤ 2 pd"
            value={s?.sla2pd?.pct != null ? `${s.sla2pd.pct}%` : '—'}
            color="#00b96b"
            sub={s?.sla2pd ? `${s.sla2pd.count}/${s.sla2pd.total} vyriešených` : undefined}
          />
          <KpiCard
            icon={<ThunderboltOutlined />}
            label="SLA ≤ 5 pd"
            value={s?.sla5pd?.pct != null ? `${s.sla5pd.pct}%` : '—'}
            color="#52c41a"
            sub={s?.sla5pd ? `${s.sla5pd.count}/${s.sla5pd.total} vyriešených` : undefined}
          />
          <KpiCard
            icon={<RiseOutlined />}
            label="Vytvorené / Vyriešené"
            value={io ? `${io.created}/${io.resolved}` : '—'}
            color={io && io.balance > 0 ? '#e88e39' : '#00b96b'}
            sub={io ? `saldo ${io.balance > 0 ? '+' : ''}${io.balance}` : undefined}
          />
          {hasEffort && (
            <KpiCard
              icon={<BarChartOutlined />}
              label="MD plán / skutočnosť"
              value={`${fmtMd(effort.plannedSum)} / ${fmtMd(effort.actualSum)}`}
              color="#a78bfa"
              sub={`priemer ${fmtMd(effort.plannedAvg)} → ${fmtMd(effort.actualAvg)}`}
            />
          )}
        </div>
        )}

        {/* ── monthly + slowest ── */}
        <div className="kpi-main-grid">

          {/* monthly bars */}
          <div className="kpi-section">
            <div className="kpi-section-title">Vybavené po mesiacoch</div>
            {loading ? (
              <SkelLines count={8} />
            ) : byMonth.length === 0 ? (
              <div className="kpi-empty">Žiadne dáta</div>
            ) : (
              <FadeIn>
              <div className="kpi-bars">
                {[...byMonth].reverse().map(m => {
                  const pct = Math.round((m.count / maxCount) * 100);
                  return (
                    <div key={`${m.year}-${m.month}`} className="kpi-bar-row">
                      <div className="kpi-bar-label">{SK_MONTHS[m.month - 1]} {m.year}</div>
                      <div className="kpi-bar-track">
                        <div className="kpi-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="kpi-bar-count">{m.count}</div>
                      <div className="kpi-bar-time">{fmtTime(m.avgMinutes)}</div>
                    </div>
                  );
                })}
              </div>
              </FadeIn>
            )}
          </div>

          {/* slowest */}
          <div className="kpi-section">
            <div className="kpi-section-title">
              Najdlhšie riešené
              {periodNote && <span className="kpi-section-filter-note">{periodNote}</span>}
            </div>
            {loading ? (
              <SkelList rows={8} />
            ) : !data?.slowest?.length ? (
              <div className="kpi-empty">Žiadne dáta</div>
            ) : (
              <FadeIn>
              <div className="kpi-slowest-list">
                {data.slowest.map((inc, i) => (
                  <div
                    key={inc.id}
                    className="kpi-slowest-row"
                    style={{ cursor: 'pointer' }}
                    onClick={() => openIncModal(inc.id)}
                    title={inc.text}
                  >
                    <span className="kpi-slowest-rank">#{i + 1}</span>
                    <span className="kpi-slowest-niceid">{inc.niceid}</span>
                    <span className="kpi-slowest-text">{inc.text}</span>
                    {inc.category && <span className="kpi-slowest-cat">{inc.category}</span>}
                    <span className="kpi-slowest-time" title={`${inc.created} → ${inc.completionDate}`}>
                      {fmtTime(inc.resolutionMinutes)}
                    </span>
                  </div>
                ))}
              </div>
              </FadeIn>
            )}
          </div>
        </div>

        {/* ── longest open + priority/bug ── */}
        <div className="kpi-main-grid">

          <div className="kpi-section">
            <div className="kpi-section-title">
              Najdlhšie otvorené
              {periodNote && <span className="kpi-section-filter-note">{periodNote}</span>}
            </div>
            {loading ? (
              <SkelList rows={8} />
            ) : !data?.longestOpen?.length ? (
              <div className="kpi-empty">Žiadne otvorené požiadavky</div>
            ) : (
              <FadeIn>
              <div className="kpi-slowest-list">
                {data.longestOpen.map((inc, i) => {
                  const sm = STATUS_META[inc.status] || STATUS_META[1];
                  return (
                    <div
                      key={inc.id}
                      className="kpi-slowest-row"
                      style={{ cursor: 'pointer' }}
                      onClick={() => openIncModal(inc.id)}
                      title={inc.text}
                    >
                      <span className="kpi-slowest-rank">#{i + 1}</span>
                      <span className="kpi-slowest-niceid">{inc.niceid}</span>
                      <span className="kpi-slowest-text">{inc.text}</span>
                      <span className="kpi-slowest-cat" style={{ color: sm.color }}>{sm.label}</span>
                      {inc.category && <span className="kpi-slowest-cat">{inc.category}</span>}
                      <span className="kpi-slowest-time kpi-open-age" title={`od ${inc.created}`}>
                        {fmtTime(inc.openMinutes)}
                      </span>
                    </div>
                  );
                })}
              </div>
              </FadeIn>
            )}
          </div>

          <div className="kpi-section">
            <div className="kpi-section-title">Priorita &amp; bug reporty</div>
            {loading ? (
              <SkelTable rows={4} cols={5} />
            ) : (
              <FadeIn>
              <>
                <table className="kpi-table kpi-compact-table">
                  <thead>
                    <tr>
                      <th>Priorita</th>
                      <th>Celkom</th>
                      <th>Otvorené</th>
                      <th>Vyriešené</th>
                      <th>Priem. čas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.byPriority || []).map(row => (
                      <tr key={row.priority}>
                        <td className="kpi-cat-name" style={{ color: row.priority === 1 ? '#ff5a5a' : '#aaa' }}>
                          {PRIORITY_LABEL[row.priority] || row.priority}
                        </td>
                        <td>{row.total}</td>
                        <td style={{ color: row.open > 0 ? '#e88e39' : '#555' }}>{row.open}</td>
                        <td style={{ color: '#52c41a' }}>{row.resolved}</td>
                        <td style={{ color: '#00b96b' }}>{fmtTime(row.avgMinutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="kpi-bug-split">
                  {(data?.byBug || []).map(row => (
                    <div key={row.isBug} className="kpi-bug-card">
                      <div className="kpi-bug-card-title" style={{ color: row.isBug ? '#ff5a5a' : '#5abbff' }}>
                        {row.isBug ? <BugOutlined /> : <CheckCircleOutlined />}
                        {row.isBug ? 'Bug reporty' : 'Bežné požiadavky'}
                      </div>
                      <div className="kpi-bug-card-stats">
                        <span><strong>{row.total}</strong> celkom</span>
                        <span style={{ color: '#e88e39' }}>{row.open} otvor.</span>
                        <span style={{ color: '#52c41a' }}>{row.resolved} vyrieš.</span>
                        <span style={{ color: '#00b96b' }}>{fmtTime(row.avgMinutes)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
              </FadeIn>
            )}
          </div>
        </div>

        {/* ── requesters + category side by side ── */}
        <div className="kpi-main-grid">

          {/* top requesters */}
          <div className="kpi-section">
            <div className="kpi-section-title">
              <TeamOutlined style={{ marginRight: 6 }} />
              Najčastejší žiadatelia
            </div>
            {loading ? (
              <SkelTable rows={8} cols={4} />
            ) : !data?.topRequesters?.length ? (
              <div className="kpi-empty">Žiadne dáta</div>
            ) : (
              <FadeIn>
              <table className="kpi-table">
                <thead>
                  <tr>
                    <th style={{ width: 28 }}>#</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortReq('email')}>
                      Email <SortIcon field="email" sortField={sortReq.field} sortOrder={sortReq.order} />
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortReq('total')}>
                      Požiadavky <SortIcon field="total" sortField={sortReq.field} sortOrder={sortReq.order} />
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortReq('resolved')}>
                      Vyriešené <SortIcon field="resolved" sortField={sortReq.field} sortOrder={sortReq.order} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRequesters.map((r, i) => (
                    <tr key={r.email}>
                      <td style={{ color: '#555' }}>#{i + 1}</td>
                      <td className="kpi-cat-name" style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.email}</td>
                      <td><strong style={{ color: '#aaa' }}>{r.total}</strong></td>
                      <td style={{ color: '#52c41a' }}>{r.resolved}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </FadeIn>
            )}
          </div>

          {/* category breakdown */}
          <div className="kpi-section">
            <div className="kpi-section-title">
              <StarOutlined style={{ marginRight: 6 }} />
              Štatistiky podľa projektu
            </div>
            {loading ? (
              <SkelTable rows={8} cols={5} />
            ) : !data?.byCategory?.length ? (
              <div className="kpi-empty">Žiadne dáta</div>
            ) : (
              <FadeIn>
              <table className="kpi-table">
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortCat('category')}>
                      Kategória <SortIcon field="category" sortField={sortCat.field} sortOrder={sortCat.order} />
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortCat('total')}>
                      Celkom <SortIcon field="total" sortField={sortCat.field} sortOrder={sortCat.order} />
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortCat('open')}>
                      Otvorené <SortIcon field="open" sortField={sortCat.field} sortOrder={sortCat.order} />
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortCat('resolved')}>
                      % vybavenia <SortIcon field="resolved" sortField={sortCat.field} sortOrder={sortCat.order} />
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortCat('avgMinutes')}>
                      Priem. čas <SortIcon field="avgMinutes" sortField={sortCat.field} sortOrder={sortCat.order} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCategories.map((row) => {
                    const total = Number(row.total) || 0;
                    const resolvedPct = total > 0 ? Math.round((Number(row.resolved) / total) * 100) : 0;
                    return (
                      <tr key={row.category}>
                        <td className="kpi-cat-name">
                          {row.category === topCatName && !category && (
                            <StarOutlined style={{ color: '#a78bfa', marginRight: 4, fontSize: 10 }} />
                          )}
                          {row.category}
                        </td>
                        <td>{total}</td>
                        <td style={{ color: row.open > 0 ? '#e88e39' : '#555' }}>{row.open || 0}</td>
                        <td>
                          <div className="kpi-pct-bar">
                            <div className="kpi-pct-fill" style={{ width: `${resolvedPct}%` }} />
                            <span className="kpi-pct-label">{resolvedPct}%</span>
                          </div>
                        </td>
                        <td style={{ color: '#00b96b' }}>{fmtTime(row.avgMinutes)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </FadeIn>
            )}
          </div>
        </div>

      </div>

      {/* ── KPI list modal ── */}
      <Modal
        title={<span style={{ color: '#e0e0dc' }}>{listModal.title}</span>}
        open={listModal.open}
        onCancel={() => setListModal({ open: false, title: '', status: null })}
        footer={null}
        width={920}
        styles={{ body: { maxHeight: '65vh', overflowY: 'auto', padding: '12px 16px', background: '#1e1f22' }, content: { background: '#1e1f22', border: '1px solid #2e2f33' }, header: { background: '#1e1f22', borderBottom: '1px solid #2e2f33' } }}
      >
        {listLoading ? (
          <SkelTable rows={8} cols={5} />
        ) : (
          <table className="kpi-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: 110 }}>ID</th>
                <th>Popis</th>
                <th style={{ width: 200 }}>Email</th>
                <th style={{ width: 100 }}>Stav</th>
                <th style={{ width: 90 }}>Vytvorené</th>
              </tr>
            </thead>
            <tbody>
              {listItems.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: '#555', padding: 24 }}>Žiadne záznamy</td></tr>
              ) : listItems.map(inc => {
                const sm = STATUS_META[inc.status] || STATUS_META[1];
                return (
                  <tr
                    key={inc.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => { setListModal(prev => ({ ...prev, open: false })); openIncModal(inc.id); }}
                  >
                    <td><span style={{ fontFamily: 'monospace', color: '#00b96b', fontSize: 12 }}>{inc.niceid}</span></td>
                    <td style={{ maxWidth: 340 }}><span style={{ fontSize: 13, color: '#ccc' }}>{inc.text}</span></td>
                    <td style={{ fontFamily: 'monospace', fontSize: 11, color: '#666' }}>{inc.email}</td>
                    <td><span style={{ color: sm.color, fontWeight: 600, fontSize: 11 }}>{sm.label}</span></td>
                    <td style={{ color: '#555', fontSize: 11 }}>
                      {inc.created ? new Date(inc.created).toLocaleDateString('sk-SK') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Modal>

      {/* ── Incident detail modal ── */}
      {selIncident && (
        <IncidentModal
          open={incModalOpen}
          incident={selIncident}
          onClose={() => { setIncModalOpen(false); setSelIncident(null); }}
          fetchIncidents={null}
        />
      )}

    </div>
  );
};

export default KPI;
